import { Component, ElementRef, NgZone, OnDestroy, OnInit, effect, inject, model, viewChild } from '@angular/core';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { GoogleChartComponent, GoogleChartInterface, Ng2GoogleChartsModule } from 'ng2-google-charts';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BaseComponent } from '../../base/base.component';
import { ThemeService } from '../../../services';

@Component({
  selector: 'app-geo-chart',
  templateUrl: './geo-chart.component.html',
  styleUrl: './geo-chart.component.scss',
  imports: [Ng2GoogleChartsModule],
})
export class GeoChartComponent extends BaseComponent implements OnInit, OnDestroy {
  private readonly viewportRuler = inject(ViewportRuler);
  private readonly ngZone = inject(NgZone);
  private readonly themeService = inject(ThemeService);
  private readonly host = inject(ElementRef);

  private readonly chart = viewChild<GoogleChartComponent>('chart');

  public readonly data = model<Observable<any>>();

  protected tableData: any[][];

  // A map already on screen has to follow a change of appearance. Google Charts paints into the
  // DOM once and does not re-read its options, so it needs redrawing.
  //
  // ⚠️ Only while it is actually on screen. This chart lives in a tab panel that keeps its content
  // when hidden, and Google Charts draws into the element it is handed: at zero size it produces an
  // EMPTY chart, and nothing would repaint it when the tab came back. Skipped here, the map keeps
  // the appearance it was drawn with until its next data load or resize.
  private readonly themeEffect = effect(() => {
    // ⚠️ Read the signal HERE, not only inside the geoChart getter. The guard below skips the
    // redraw until there is data AND the tab is on screen, and an effect that reads no signal on a
    // run ends that run with no dependencies and is never scheduled again - the map would silently
    // stop following the appearance. (Raised in review, 2026-09-17.)
    this.themeService.isDark();
    if (this.tableData && this.isOnScreen()) {
      this.redraw();
    }
  });

  ngOnInit() {
    this.getData();
    this.onResize();
  }

  ngOnDestroy() {
    this.viewportRuler.ngOnDestroy();
  }

  get geoChart(): GoogleChartInterface {
    const { width, height } = this.viewportRuler.getViewportSize();
    return {
      chartType: 'GeoChart',
      dataTable: this.tableData,
      options: {
        // Yellow-to-red carries the sales value and means the same in both appearances.
        colorAxis: { colors: ['yellow', 'red'] },
        width: width - (width > 1400 ? 304 : 64),
        height: height - 197,
        // ⚠️ Google Charts draws into the DOM from plain option values, so a CSS variable has to be
        // resolved here rather than passed through. In Light nothing is passed at all, so the map
        // keeps Google's own defaults - a white ocean and #F5F5F5 countries - exactly as before.
        ...(this.themeService.isDark()
          ? {
              backgroundColor: this.themeColour('--tms-panel-bg'),
              datalessRegionColor: this.themeColour('--tms-map-dataless'),
              // ⚠️ `auraColor: 'none'` matters: Google draws a white halo behind chart text so it
              // stays legible on a white map. Light text with a white halo on a dark map looks
              // blurred, which is exactly how it read (Derek, 2026-09-17).
              legend: { textStyle: { color: this.themeColour('--tms-text'), auraColor: 'none' } },
            }
          : {}),
      },
    };
  }

  private themeColour(name: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }

  private isOnScreen(): boolean {
    return (this.host.nativeElement as HTMLElement).getBoundingClientRect().width > 0;
  }

  private getData() {
    if (this.data()) {
      this.data()
        .pipe(takeUntil(this.destroy$))
        .subscribe((data) => {
          if (data?.length > 1) {
            this.tableData = data;
            this.redraw();
          }
        });
    }
  }

  private onResize() {
    this.viewportRuler
      .change()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.ngZone.run(() => this.redraw()));
  }

  private redraw() {
    this.chart()?.draw(this.geoChart);
  }
}
